import { Document, Page, Text, View, StyleSheet, Image, Link } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#fff',
    paddingTop: 32,
    paddingBottom: 32,
    paddingLeft: 36,
    paddingRight: 36,
    fontSize: 11,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 18,
  },
  photo: {
    width: 80,
    height: 80,
    borderRadius: 40,
    marginRight: 18,
    objectFit: 'cover',
  },
  headerInfo: {
    flex: 1,
  },
  name: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#222',
    marginBottom: 2,
  },
  title: {
    fontSize: 13,
    color: '#444',
    marginBottom: 6,
  },
  contact: {
    fontSize: 10,
    color: '#444',
    marginBottom: 2,
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 6,
    borderBottom: '1 solid #e0e0e0',
    paddingBottom: 2,
    textTransform: 'uppercase',
  },
  content: {
    fontSize: 11,
    color: '#34495e',
    marginBottom: 4,
    lineHeight: 1.5,
  },
  bullet: {
    marginLeft: 10,
    marginBottom: 2,
  },
  techStack: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 3,
    marginBottom: 2,
  },
  techItem: {
    backgroundColor: '#e0e0e0',
    padding: '2 6',
    margin: '0 4 4 0',
    borderRadius: 3,
    fontSize: 9,
  },
  small: {
    fontSize: 10,
    color: '#555',
    marginBottom: 2,
  },
  twoCol: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  col: {
    flex: 1,
    minWidth: 200,
    marginRight: 12,
  },
  projectTitle: {
    fontWeight: 'bold',
    fontSize: 11,
    marginBottom: 1,
  },
  projectSub: {
    fontSize: 10,
    color: '#666',
    marginBottom: 1,
  },
  divider: {
    borderBottom: '1 solid #e0e0e0',
    marginVertical: 8,
  },
});

const contact = {
  name: 'AMRUTHAM SURESH KUMAR',
  title: 'Senior Software Engineer II',
  email: 'amruthamsuresh2291@gmail.com',
  phone: '+81 90-XXXX-XXXX',
  location: 'Tokyo, Japan',
  linkedin: 'https://www.linkedin.com/in/amrutham-suresh-kumar-43813b111/',
};

const summary = `Senior Software Engineer with 7+ years of experience in developing global products with expertise in Backend Development. Skilled in Node.js, MongoDB, MySQL, and AWS, with a proven track record of optimizing system performance and leading technical projects. Published researcher in Indoor Navigation and IoT with cross-cultural experience including Japanese language proficiency. Demonstrated ability to architect and implement high-performance database solutions, improving application performance by up to 40% while reducing operational costs.`;

const coreCompetencies = [
  'Backend Development: Node.js, TypeScript, Express, REST APIs, Microservices',
  'Databases: MongoDB, PostgreSQL, MySQL, TimescaleDB',
  'Cloud Services: AWS (EC2, S3, RDS, CodeDeploy, CloudWatch), GCP',
  'DevOps: CI/CD, Git, BitBucket Pipelines',
  'Additional Skills: IoT, Indoor Navigation, Payment Gateway Integration',
];

const achievements = [
  'Led development of microservices-based applications serving 100K+ daily users',
  'Reduced system response time by 40% through optimization techniques',
  'Successfully migrated 9.8+ million records with zero data loss',
  'Achieved annual cost savings of $3,000 through infrastructure optimization',
  'Published two research papers in the field of IoT and Computer Vision',
  'Developed and published NPM package for image annotation with multiple downloads',
  'Delivered projects for international clients across USA, UAE, Japan, MENA region, and India',
  'Mentored junior developers in MEAN stack development and best practices',
  'Successfully implemented AWS infrastructure from scratch for multiple organizations',
  'Streamlined project management processes through JIRA automation and custom workflows',
];

const experience = [
  {
    title: 'SENIOR SOFTWARE ENGINEER II',
    company: 'Vanix Technologies',
    location: 'Hyderabad, India',
    period: 'Oct 2024 - Present',
    details: [
      'Led backend development using Node.js and PostgreSQL, improving application performance by 40%',
      'Architected and managed database solutions using PostgreSQL and TimescaleDB',
      'Led the development of an in-house database migration solution, efficiently migrating 9.8+ million records across 60+ tables to TimescaleDB, achieving 31,000 rows per second throughput with zero data loss',
      'Reduced AWS Data Migration Service (DMS) costs by 70% and AWS Redshift costs, achieving annual savings of $3,000',
      'Coordinated comprehensive backend API planning and development, ensuring seamless integration between Firmware, Web, and Mobile App teams',
      'Established and maintained MCP JIRA Server infrastructure with automated workflows and custom scripts',
      'Designed and implemented AWS infrastructure including EC2, WAF, Load Balancer, ALB with domain routing, CloudWatch monitoring, and PITR for PostgreSQL databases',
    ],
    tech: ['Node.js', 'TypeScript', 'PostgreSQL', 'MySQL', 'Angular', 'AWS', 'Git'],
  },
  {
    title: 'SENIOR SOFTWARE ENGINEER II',
    company: 'ENGRO Technologies',
    location: 'Hyderabad, India',
    period: 'May 2023 - Dec 2023',
    details: [
      'Built ETL migration tool for SQL to MongoDB data transformation',
      'Developed subscription and payment system for OTT platform serving MENA, India, US, and UK',
      'Integrated multiple payment gateways including Adyen and Google Play',
      'Led successful AWS to GCP migration, optimizing performance for 13M+ records',
    ],
    tech: ['Node.js', 'MongoDB', 'MySQL', 'AWS', 'GCP', 'Adyen', 'In-App Purchase (iOS)', 'Google Play (Android)', 'Git'],
  },
  {
    title: 'SENIOR SOFTWARE ENGINEER',
    company: 'Kore.AI',
    location: 'Hyderabad, India',
    period: 'Oct 2020 - Sep 2022',
    details: [
      'Developed Bank Assist Workbench on Kore.ai platform for banking workflow automation',
      'Created approval interface for HR and IT management systems',
      'Implemented DevOps processes and UI enhancements',
      'Served international clients across USA, UAE, and Japan',
    ],
    tech: ['Node.js', 'MongoDB', 'Redis', 'Kore.ai Platform', 'AWS', 'Git'],
  },
  {
    title: 'NODE.JS BACK-END DEVELOPER',
    company: 'Terminal 2',
    location: 'Hyderabad, India',
    period: 'Apr 2019 - Feb 2020',
    details: [
      'Built E-commerce backend system using Node.js and MySQL',
      'Implemented REST APIs for product management',
      'Set up AWS infrastructure (EC2, RDS, S3)',
      'Created automated deployment pipeline using Bitbucket and AWS CodeDeploy',
    ],
    tech: ['Node.js', 'MySQL', 'AWS EC2', 'RDS', 'S3', 'CodeDeploy', 'Bitbucket Pipeline'],
  },
  {
    title: 'NODE.JS BACK-END DEVELOPER',
    company: 'Flappjacks',
    location: 'Hyderabad, India',
    period: 'Dec 2018 - Apr 2019',
    details: [
      'Developed backend for web and mobile applications using Node.js and MongoDB',
      'Created REST APIs for cross-platform integration',
      'Configured AWS infrastructure for production deployment',
      'Managed database operations and system maintenance',
    ],
    tech: ['Node.js', 'MongoDB', 'MySQL', 'AWS', 'BitBucket'],
  },
  {
    title: 'RESEARCH ASSISTANT',
    company: 'SRM Research Institute',
    location: 'Chennai, India',
    period: 'Jan 2016 - Dec 2018',
    details: [
      'Researched Indoor Navigation and Location Systems for IoT applications',
      'Implemented Precision Tracking using UWB technology',
      'Developed pathfinding algorithms for indoor navigation',
      'Published research findings in IoT and indoor positioning',
    ],
    tech: ['JavaScript', 'Java', 'Node.js', 'UWB', 'IoT Sensors', 'Indoor Navigation Systems'],
  },
];

const projects = [
  {
    title: 'Database Migration Tool (MySQL to TimescaleDB)',
    subtitle: 'Independent Project',
    details: [
      'Developed a powerful migration tool supporting both generic database migrations and specialized TimescaleDB migrations',
      'Implemented incremental data synchronization with progress tracking and checkpointing',
      'Created a detailed logging system and configurable migration processes',
      'Achieved migration speed of 31,000 rows per second with zero data loss',
      'Reduced operational costs by 70% compared to AWS DMS',
    ],
    tech: ['Node.js', 'TypeScript', 'MySQL', 'PostgreSQL', 'TimescaleDB', 'Docker', 'AWS DMS'],
  },
  {
    title: 'Japanese Grammar Learning Platform',
    subtitle: 'Personal Project',
    details: [
      'Built an interactive web application for learning Japanese grammar patterns from the Minna no Nihongo textbook series',
      'Implemented features including browsing by lesson, furigana display, multi-language translations, and visual illustrations',
      'Created responsive design for desktop and mobile devices with search functionality',
    ],
    tech: ['React', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Vercel', 'i18n', 'Framer Motion'],
  },
  {
    title: 'Photo Annotate',
    subtitle: 'Full Stack MERN Application',
    details: [
      'Developed a comprehensive platform for sharing, annotating, and downloading images',
      'Implemented advanced features including user authentication, social features (likes, comments, follow), and friend request system',
      'Created profile management system with dark/light mode toggle',
    ],
    tech: ['JavaScript', 'Node.js', 'MongoDB', 'React', 'React-Konva', 'AWS', 'Cloudinary', 'FastQ', 'JWT', 'Mongoose', 'Multer', 'Nodemailer', 'Sharp'],
  },
  {
    title: 'React Image Annotate Tool',
    subtitle: 'NPM Package',
    details: [
      'Created a React component published as an NPM package for image annotation',
      'Implemented features including text and circle annotations, freehand drawing, font customization, and color picker integration',
      'Added undo/redo functionality and image download capabilities with cross-browser compatibility',
    ],
    tech: ['React', 'JavaScript', 'NPM', 'ReactKonva', 'HTML5', 'CSS3'],
  },
];

const research = [
  '"Indoor Crowd Detection Using RGB Camera And IoT" (IJESMR)',
  '"IoT Concept for Animal Detection Using ANN to Prevent Animal Vehicle Collision on Highways" (IJSEA)',
];

const certifications = [
  'AWS Certified Developer – Associate (DVA-C01)',
  'Google Professional Cloud Developer',
  'MongoDB Basics - MongoDB University',
  'JLPT N5 Certification in Japanese Language',
];

const education = {
  degree: 'Bachelor of Technology in Computer Science & Engineering',
  university: 'SRM University, Chennai, India',
  period: '2012 - 2016',
};

const languages = [
  'English (Professional)',
  'Telugu (Native)',
  'Hindi (Fluent)',
  'Japanese (Basic - JLPT N5 Certified)',
];

const references = 'Available upon request';

const Resume = () => (
  <Document>
    {/* Page 1: Header, Summary, Core Competencies, Achievements */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Image src="/mypic.png" style={styles.photo} />
        <View style={styles.headerInfo}>
          <Text style={styles.name}>{contact.name}</Text>
          <Text style={styles.title}>{contact.title}</Text>
          <Text style={styles.contact}>{contact.location}</Text>
          <Text style={styles.contact}>{contact.email}</Text>
          <Text style={styles.contact}>{contact.phone}</Text>
          <Link src={contact.linkedin} style={styles.contact}>LinkedIn</Link>
        </View>
      </View>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Professional Summary</Text>
        <Text style={styles.content}>{summary}</Text>
      </View>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Core Competencies</Text>
        {coreCompetencies.map((item, i) => (
          <Text key={i} style={styles.content}>• {item}</Text>
        ))}
      </View>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Key Achievements</Text>
        {achievements.map((item, i) => (
          <Text key={i} style={styles.content}>• {item}</Text>
        ))}
      </View>
    </Page>
    {/* Page 2: Professional Experience */}
    <Page size="A4" style={styles.page}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Professional Experience</Text>
        {experience.map((exp, idx) => (
          <View key={idx} style={{ marginBottom: 10 }}>
            <Text style={styles.projectTitle}>{exp.title} | {exp.company}</Text>
            <Text style={styles.projectSub}>{exp.period} | {exp.location}</Text>
            {exp.details.map((d, i) => (
              <Text key={i} style={styles.content}>• {d}</Text>
            ))}
            <View style={styles.techStack}>
              {exp.tech.map((t, i) => (
                <Text key={i} style={styles.techItem}>{t}</Text>
              ))}
            </View>
          </View>
        ))}
      </View>
    </Page>
    {/* Page 3: Projects, Research, Education, Certifications, Languages, References */}
    <Page size="A4" style={styles.page}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Key Projects</Text>
        {projects.map((proj, idx) => (
          <View key={idx} style={{ marginBottom: 8 }}>
            <Text style={styles.projectTitle}>{proj.title}</Text>
            <Text style={styles.projectSub}>{proj.subtitle}</Text>
            {proj.details.map((d, i) => (
              <Text key={i} style={styles.content}>• {d}</Text>
            ))}
            <View style={styles.techStack}>
              {proj.tech.map((t, i) => (
                <Text key={i} style={styles.techItem}>{t}</Text>
              ))}
            </View>
          </View>
        ))}
      </View>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Research Publications</Text>
        {research.map((paper, i) => (
          <Text key={i} style={styles.content}>• {paper}</Text>
        ))}
      </View>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Education</Text>
        <Text style={styles.content}>{education.degree}</Text>
        <Text style={styles.content}>{education.university}</Text>
        <Text style={styles.content}>{education.period}</Text>
      </View>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Certifications & Training</Text>
        {certifications.map((cert, i) => (
          <Text key={i} style={styles.content}>• {cert}</Text>
        ))}
      </View>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Languages</Text>
        {languages.map((lang, i) => (
          <Text key={i} style={styles.content}>• {lang}</Text>
        ))}
      </View>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>References</Text>
        <Text style={styles.content}>{references}</Text>
      </View>
    </Page>
  </Document>
);

export default Resume;

